import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { HiChevronDown, HiSparkles } from "react-icons/hi2";
import { FloorPlan3DPreset, editorStore } from "../../store/editorStore";
import { userStore } from "../../store/userStore";
import { FLOORPLAN_AI_RENDER_CREDIT_COST } from "../../gql/floorplanAIRender";

interface CameraViewControlsProps {
  className?: string;
}

const CameraViewControls: React.FC<CameraViewControlsProps> = observer(({ className }) => {
  const { t } = useTranslation();
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const sideMenuRef = useRef<HTMLDivElement | null>(null);

  const primaryPresets: Array<{ id: FloorPlan3DPreset; label: string; tooltip: string }> = [
    {
      id: "dollhouse",
      label: t("cameraViews.perspectiveView"),
      tooltip: t("cameraViews.perspectiveView"),
    },
    {
      id: "birdsEye",
      label: t("cameraViews.planView"),
      tooltip: t("cameraViews.planView"),
    },
  ];

  const sidePresets: Array<{ id: FloorPlan3DPreset; label: string; tooltip: string }> = [
    {
      id: "front",
      label: t("cameraViews.frontView"),
      tooltip: t("cameraViews.frontSideView"),
    },
    {
      id: "back",
      label: t("cameraViews.backView"),
      tooltip: t("cameraViews.backSideView"),
    },
    {
      id: "left",
      label: t("cameraViews.leftView"),
      tooltip: t("cameraViews.leftSideView"),
    },
    {
      id: "right",
      label: t("cameraViews.rightView"),
      tooltip: t("cameraViews.rightSideView"),
    },
  ];

  useEffect(() => {
    if (!sideMenuOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!sideMenuRef.current?.contains(event.target as Node)) {
        setSideMenuOpen(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [sideMenuOpen]);

  if (!editorStore.view3D) {
    return null;
  }

  const activeSidePreset = sidePresets.find((preset) => preset.id === editorStore.view3DPreset);
  const sideMenuActive = Boolean(activeSidePreset);

  const credits = userStore.entitlements?.aiCredits;
  const outOfCredits = Boolean(
    credits &&
      !credits.unlimited &&
      (credits.remaining ?? 0) < FLOORPLAN_AI_RENDER_CREDIT_COST,
  );

  const openRenderModal = () => {
    editorStore.openRender3DViewModal();
  };

  const segmentClass =
    "flex h-full min-w-[4.5rem] shrink-0 items-center justify-center gap-1 px-2 text-xs font-semibold leading-none transition-colors sm:min-w-[132px] sm:gap-2 sm:px-4 sm:text-sm";

  return (
    <div
      className={`flex h-[42px] items-stretch select-none ${className ?? ""}`}
      aria-label={t("cameraViews.controlsAriaLabel")}
    >
      <div className="flex h-full min-w-0 items-stretch overflow-visible rounded-md border border-gray-300 bg-white shadow-md">
        {primaryPresets.map((preset, index) => {
          const active = editorStore.view3DPreset === preset.id;

          return (
            <button
              key={preset.id}
              type="button"
              className={`${segmentClass} ${index === 0 ? "rounded-l-md" : ""} ${
                active
                  ? "bg-boniYellowPrimary text-blackplum"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
              onClick={() => editorStore.setView3DPreset(preset.id)}
              aria-pressed={active}
              title={preset.tooltip}
            >
              <span className="truncate">{preset.label}</span>
            </button>
          );
        })}

        <div ref={sideMenuRef} className="relative h-full shrink-0 border-l border-gray-200">
          <button
            type="button"
            className={`${segmentClass} ${
              sideMenuActive
                ? "bg-boniYellowPrimary text-blackplum"
                : "bg-white text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => setSideMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={sideMenuOpen}
            title={activeSidePreset?.tooltip ?? t("cameraViews.elevationViews")}
          >
            <span className="truncate">
              {activeSidePreset?.label ?? t("cameraViews.elevationViews")}
            </span>
            <HiChevronDown
              className={`h-4 w-4 shrink-0 transition-transform ${sideMenuOpen ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>

          {sideMenuOpen && (
            <div
              className="absolute bottom-[calc(100%+8px)] right-0 z-[10000] w-[164px] overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
              role="menu"
              aria-label={t("cameraViews.elevationPresetsAriaLabel")}
            >
              {sidePresets.map((preset) => {
                const active = editorStore.view3DPreset === preset.id;

                return (
                  <button
                    key={preset.id}
                    type="button"
                    className={`block w-full px-4 py-2 text-left text-sm font-semibold transition-colors ${
                      active
                        ? "bg-boniYellowPrimary text-blackplum"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    onClick={() => {
                      editorStore.setView3DPreset(preset.id);
                      setSideMenuOpen(false);
                    }}
                    role="menuitem"
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="button"
          className={`${segmentClass} rounded-r-md border-l border-gray-200 bg-white text-gray-700 hover:bg-gray-100`}
          onClick={openRenderModal}
          title={
            outOfCredits
              ? t("cameraViews.notEnoughCreditsTooltip")
              : t("cameraViews.renderTooltip")
          }
          aria-label={t("cameraViews.renderAriaLabel")}
        >
          <HiSparkles className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">{t("cameraViews.render")}</span>
        </button>
      </div>
    </div>
  );
});

export default CameraViewControls;
